import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, patch, post, put, uploadFile, del, openDmsFile, API } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Skeleton } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Avatar } from "@/components/common/Avatar";
import { Section } from "@/components/common/PageHeader";
import { DmsFileActions } from "@/components/common/DmsFileActions";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { money, dateFmt, relative } from "@/lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Pencil, Plus, Lock, FileText, FileCog, Printer, X, LogOut, Trash2, SlidersHorizontal, Flag, AlertTriangle, Upload, Mail } from "lucide-react";
import { UploadProgress } from "@/components/common/UploadProgress";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { toast } from "sonner";

const EVENT_TYPES = ["designation_change", "transfer", "promotion", "salary_revision"];
const EMPLOYMENT_TYPES = ["full_time", "part_time", "contract", "intern"];

export default function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { me, can } = useApp();
  const { data: emp, isLoading } = useQuery({ queryKey: ["employee", id], queryFn: () => get(`/hrms/employees/${id}`) });
  const { data: leave } = useQuery({ queryKey: ["leave"], queryFn: () => get("/hrms/leave") });
  const { data: payroll = [] } = useQuery({ queryKey: ["payroll"], queryFn: () => get("/hrms/payroll") });
  const { data: docs = [] } = useQuery({ queryKey: ["dms-files", "employee", id], queryFn: () => get(`/dms/files?entity_type=employee&entity_id=${id}`) });
  const { data: myAssets = [] } = useQuery({ queryKey: ["assets", id], queryFn: () => get(`/hrms/assets?employee_id=${id}`) });
  const { data: perfTemplate } = useQuery({ queryKey: ["employee-template", id], queryFn: () => get(`/hrms/performance/employee-template/${id}`) });

  const [dimensions, setDimensions] = useState(null);
  useEffect(() => { if (perfTemplate && !dimensions) setDimensions(perfTemplate.dimensions.map((d) => ({ ...d }))); }, [perfTemplate, dimensions]);

  const saveTemplate = useMutation({
    mutationFn: () => put(`/hrms/performance/employee-template/${id}`, { dimensions }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["employee-template", id] }); toast.success("Performance parameters saved"); },
  });

  const { data: flags = [] } = useQuery({ queryKey: ["employee-flags", id], queryFn: () => get(`/hrms/employees/${id}/flags`), enabled: can("hrms", "override") });
  const [addingFlag, setAddingFlag] = useState(false);
  const [flagForm, setFlagForm] = useState({ reason: "", details: "" });
  const addFlag = useMutation({
    mutationFn: (body) => post(`/hrms/employees/${id}/flags`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["employee-flags", id] });
      qc.invalidateQueries({ queryKey: ["employee", id] });
      toast.success("Flag recorded");
      setAddingFlag(false);
      setFlagForm({ reason: "", details: "" });
    },
  });

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [addingHistory, setAddingHistory] = useState(false);
  const [historyForm, setHistoryForm] = useState({ event_type: "designation_change", from_value: "", to_value: "", note: "" });
  const [addingDoc, setAddingDoc] = useState(false);
  const [docForm, setDocForm] = useState({ name: "", tags: "", expiry_date: "", visibility: "all" });
  const [payslip, setPayslip] = useState(null);

  useEffect(() => { if (emp && !editing) setForm(emp); }, [emp, editing]);

  const saveProfile = useMutation({
    mutationFn: (body) => patch(`/users/${id}`, { ...body, _actor: me?.id }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["employee", id] }); qc.invalidateQueries({ queryKey: ["hrms-directory"] }); toast.success("Profile updated"); setEditing(false); },
  });

  const addHistory = useMutation({
    mutationFn: (body) => post(`/hrms/employees/${id}/history`, { ...body, _actor: me?.id }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["employee", id] }); toast.success("History entry added"); setAddingHistory(false); setHistoryForm({ event_type: "designation_change", from_value: "", to_value: "", note: "" }); },
  });

  const [sigProgress, setSigProgress] = useState(null);
  const [sigBust, setSigBust] = useState(0);
  const uploadSignature = useMutation({
    mutationFn: (fd) => uploadFile(`/hrms/employees/${id}/signature`, fd, setSigProgress),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["employee", id] }); toast.success("Signature uploaded"); setSigProgress(null); setSigBust((b) => b + 1); },
    onError: (e) => { setSigProgress(null); toast.error(e?.response?.data?.detail || "Could not upload signature"); },
  });
  const removeSignature = useMutation({
    mutationFn: () => del(`/hrms/employees/${id}/signature`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["employee", id] }); toast.success("Signature removed"); },
  });

  const toggleMailboxSummary = useMutation({
    mutationFn: (enabled) => patch(`/hrms/employees/${id}/mailbox-settings`, { mailbox_summary_enabled: enabled }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["employee", id] }); toast.success("Mailbox settings updated"); },
  });

  const [docFile, setDocFile] = useState(null);
  const [docProgress, setDocProgress] = useState(null);
  const addDoc = useMutation({
    mutationFn: (fd) => uploadFile("/dms/files", fd, setDocProgress),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["dms-files", "employee", id] }); toast.success("Document uploaded"); setAddingDoc(false); setDocForm({ name: "", tags: "", expiry_date: "", visibility: "all" }); setDocFile(null); setDocProgress(null); },
    onError: (e) => { setDocProgress(null); toast.error(e?.response?.data?.detail || "Could not upload document"); },
  });

  const initiateOffboarding = useMutation({
    mutationFn: () => post("/hrms/checklists", { employee_id: id, type: "offboarding", _actor: me?.id }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["employee", id] }); toast.success("Offboarding initiated"); navigate("/hrms/offboarding"); },
  });

  if (isLoading || !emp || !form) return <Skeleton className="h-96" />;
  const myLeave = (leave?.applications || []).filter((a) => a.employee_id === id);
  const myPay = payroll.filter((p) => p.employee_id === id);
  const canEdit = can("hrms", "edit");
  const canDelete = can("hrms", "delete");
  const canSeeHrOnly = can("hrms", "override");
  const visibleDocs = docs.filter((d) => d.visibility !== "hr_only" || canSeeHrOnly);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNested = (group, k, v) => setForm((f) => ({ ...f, [group]: { ...(f[group] || {}), [k]: v } }));

  const field = (label, key, opts = {}) => (
    <div>
      <Label>{label}</Label>
      {editing ? (
        <Input data-testid={`edit-${key}`} className="mt-1" type={opts.type || "text"}
          value={form[key] ?? ""} onChange={(e) => set(key, e.target.value)} />
      ) : (
        <div className="mt-0.5 text-sm font-medium">{emp[key] || "—"}</div>
      )}
    </div>
  );

  return (
    <div>
      <div className="mb-2 text-xs text-muted-foreground">
        <button onClick={() => navigate("/hrms/directory")} className="hover:underline">Directory</button> / {emp.name}
      </div>
      <div className="mb-6 flex items-center gap-4">
        <Avatar user={emp} size="lg" />
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">{emp.name}</h1>
          <div className="text-sm text-muted-foreground">{emp.designation} · {emp.department?.name}</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <StatusBadge status={emp.status} />
          {canEdit && !emp.exit_date && (
            <Button size="sm" variant="outline" data-testid="initiate-offboarding-btn" onClick={() => initiateOffboarding.mutate()} disabled={initiateOffboarding.isPending}>
              <LogOut className="mr-1.5 h-3.5 w-3.5" />Initiate Offboarding
            </Button>
          )}
          {canEdit && !editing && (
            <Button size="sm" variant="outline" data-testid="edit-profile-btn" onClick={() => setEditing(true)}>
              <Pencil className="mr-1.5 h-3.5 w-3.5" />Edit Profile
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="profile">
        <TabsList data-testid="emp-tabs">
          <TabsTrigger value="profile" data-testid="emp-tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="history" data-testid="emp-tab-history">Employment History</TabsTrigger>
          <TabsTrigger value="documents" data-testid="emp-tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="leave" data-testid="emp-tab-leave">Leave</TabsTrigger>
          <TabsTrigger value="payslips" data-testid="emp-tab-payslips">Payslips</TabsTrigger>
          <TabsTrigger value="assets" data-testid="emp-tab-assets">Assets</TabsTrigger>
          {canEdit && <TabsTrigger value="perf-params" data-testid="emp-tab-perf-params">Performance Parameters</TabsTrigger>}
          {canSeeHrOnly && <TabsTrigger value="flags" data-testid="emp-tab-flags">Flags{flags.length > 0 ? ` (${flags.length})` : ""}</TabsTrigger>}
        </TabsList>

        <TabsContent value="profile" className="mt-4 space-y-4">
          <Section title="Personal Details">
            <div className="grid grid-cols-2 gap-4 p-4">
              {field("Email", "email")}
              {field("Phone", "phone")}
              {field("Date of Birth", "dob", { type: "date" })}
              <div>
                <Label>Gender</Label>
                {editing ? (
                  <Select value={form.gender || ""} onValueChange={(v) => set("gender", v)}>
                    <SelectTrigger className="mt-1" data-testid="edit-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{["male", "female", "other"].map((g) => <SelectItem key={g} value={g} className="capitalize">{g}</SelectItem>)}</SelectContent>
                  </Select>
                ) : <div className="mt-0.5 text-sm font-medium capitalize">{emp.gender || "—"}</div>}
              </div>
              {field("Address", "address")}
              <div>
                <Label className="flex items-center gap-1"><Lock className="h-3 w-3" />Designation</Label>
                {editing && canEdit ? (
                  <Input data-testid="edit-designation" className="mt-1" value={form.designation ?? ""} onChange={(e) => set("designation", e.target.value)} />
                ) : <div className="mt-0.5 text-sm font-medium">{emp.designation || "—"}</div>}
              </div>
            </div>
          </Section>

          <Section title="Emergency Contact">
            <div className="grid grid-cols-3 gap-4 p-4">
              {["name", "relation", "phone"].map((k) => (
                <div key={k}>
                  <Label className="capitalize">{k}</Label>
                  {editing ? (
                    <Input data-testid={`edit-emergency-${k}`} className="mt-1" value={form.emergency_contact?.[k] ?? ""} onChange={(e) => setNested("emergency_contact", k, e.target.value)} />
                  ) : <div className="mt-0.5 text-sm font-medium">{emp.emergency_contact?.[k] || "—"}</div>}
                </div>
              ))}
            </div>
          </Section>

          <Section title="Bank Details">
            <div className="grid grid-cols-2 gap-4 p-4">
              {[["account_name", "Account Name"], ["account_number", "Account Number"], ["bank_name", "Bank Name"], ["ifsc_or_swift", "IFSC / SWIFT"]].map(([k, label]) => (
                <div key={k}>
                  <Label>{label}</Label>
                  {editing ? (
                    <Input data-testid={`edit-bank-${k}`} className="mt-1" value={form.bank_details?.[k] ?? ""} onChange={(e) => setNested("bank_details", k, e.target.value)} />
                  ) : <div className="mt-0.5 text-sm font-medium">{emp.bank_details?.[k] || "—"}</div>}
                </div>
              ))}
            </div>
          </Section>

          <Section title="Employment">
            <div className="grid grid-cols-2 gap-4 p-4">
              <div>
                <Label>Employment Type</Label>
                {editing ? (
                  <Select value={form.employment_type || ""} onValueChange={(v) => set("employment_type", v)}>
                    <SelectTrigger className="mt-1" data-testid="edit-employment_type"><SelectValue /></SelectTrigger>
                    <SelectContent>{EMPLOYMENT_TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t.replace("_", " ")}</SelectItem>)}</SelectContent>
                  </Select>
                ) : <div className="mt-0.5 text-sm font-medium capitalize">{(emp.employment_type || "—").replace("_", " ")}</div>}
              </div>
              <div><Label>Joining Date</Label><div className="mt-0.5 text-sm font-medium">{dateFmt(emp.joining_date)}</div></div>
              <div><Label>Manager</Label><div className="mt-0.5 text-sm font-medium">{emp.manager_name || "—"}</div></div>
              <div>
                <Label className="flex items-center gap-1"><Lock className="h-3 w-3" />Base Salary</Label>
                {editing && canEdit ? (
                  <Input data-testid="edit-base_salary" type="number" className="mt-1" value={form.base_salary ?? ""} onChange={(e) => set("base_salary", Number(e.target.value))} />
                ) : <div className="mt-0.5 text-sm font-medium">{emp.base_salary ? money(emp.base_salary) : "—"}</div>}
              </div>
            </div>
          </Section>

          <Section title="Signature" action={canEdit && <span className="text-xs text-muted-foreground">Used to stamp documents where this employee is the designated signatory</span>}>
            <div className="flex items-center gap-4 p-4">
              {emp.signature_r2_key ? (
                <img src={`${API}/hrms/employees/${id}/signature?v=${sigBust}`} alt={`${emp.name}'s signature`}
                  data-testid="employee-signature-preview" className="h-14 max-w-[180px] rounded border border-border bg-white object-contain p-1.5" />
              ) : (
                <span className="text-sm text-muted-foreground">No signature on file.</span>
              )}
              {canEdit && (
                <div className="flex items-center gap-2">
                  <input type="file" accept="image/png,image/jpeg" data-testid="employee-signature-file" className="hidden" id="employee-signature-input"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      const fd = new FormData();
                      fd.append("file", f);
                      uploadSignature.mutate(fd);
                    }} />
                  <Button size="sm" variant="outline" data-testid="employee-signature-upload-btn" disabled={uploadSignature.isPending}
                    onClick={() => document.getElementById("employee-signature-input").click()}>
                    <Upload className="mr-1.5 h-3.5 w-3.5" />{emp.signature_r2_key ? "Replace" : "Upload"}
                  </Button>
                  {emp.signature_r2_key && (
                    <ConfirmDelete testid="del-employee-signature" title={`Remove ${emp.name}'s signature?`}
                      description="Document templates pointing at this employee as signatory will stop stamping a signature until a new image is uploaded."
                      trigger={<button className="text-muted-foreground hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
                      onConfirm={() => removeSignature.mutate()} />
                  )}
                </div>
              )}
            </div>
            {sigProgress != null && <div className="px-4 pb-3"><UploadProgress percent={sigProgress} /></div>}
          </Section>

          <Section title="Mailbox">
            <div className="space-y-3 p-4">
              <div className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 text-muted-foreground" />
                {emp.google_connected ? (
                  <span>Connected — <span className="font-medium">{emp.google_account_email}</span></span>
                ) : (
                  <span className="text-muted-foreground">Not connected — employees connect their own Google account from the Mailbox module.</span>
                )}
              </div>
              {canEdit && (
                <div className="flex items-center justify-between rounded-lg border border-border/60 px-3 py-2.5">
                  <div>
                    <div className="text-sm font-medium">AI mail summarization</div>
                    <div className="text-xs text-muted-foreground">Not active yet (no AI provider configured) — this just controls who's opted in once one is.</div>
                  </div>
                  <Switch data-testid="mailbox-summary-toggle" checked={!!emp.mailbox_summary_enabled}
                    onCheckedChange={(v) => toggleMailboxSummary.mutate(v)} />
                </div>
              )}
            </div>
          </Section>

          {editing && (
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => { setEditing(false); setForm(emp); }}>Cancel</Button>
              <Button data-testid="save-profile-btn" onClick={() => saveProfile.mutate(form)} disabled={saveProfile.isPending}>Save Changes</Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <Section title="Employment History" action={canEdit && (
            <Button size="sm" variant="outline" data-testid="add-history-btn" onClick={() => setAddingHistory((s) => !s)}>
              <Plus className="mr-1.5 h-3.5 w-3.5" />Add Entry
            </Button>
          )}>
            {addingHistory && (
              <div className="border-b border-border/60 bg-muted/30 p-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Event Type</Label>
                    <Select value={historyForm.event_type} onValueChange={(v) => setHistoryForm((f) => ({ ...f, event_type: v }))}>
                      <SelectTrigger className="mt-1" data-testid="history-event_type"><SelectValue /></SelectTrigger>
                      <SelectContent>{EVENT_TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t.replace("_", " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div />
                  <div><Label>From</Label><Input data-testid="history-from" className="mt-1" value={historyForm.from_value} onChange={(e) => setHistoryForm((f) => ({ ...f, from_value: e.target.value }))} /></div>
                  <div><Label>To</Label><Input data-testid="history-to" className="mt-1" value={historyForm.to_value} onChange={(e) => setHistoryForm((f) => ({ ...f, to_value: e.target.value }))} /></div>
                  <div className="col-span-2"><Label>Note</Label><Input data-testid="history-note" className="mt-1" value={historyForm.note} onChange={(e) => setHistoryForm((f) => ({ ...f, note: e.target.value }))} /></div>
                </div>
                <div className="mt-3 flex justify-end gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setAddingHistory(false)}>Cancel</Button>
                  <Button size="sm" data-testid="history-submit" onClick={() => addHistory.mutate(historyForm)}>Add</Button>
                </div>
              </div>
            )}
            {emp.history?.length ? (
              <div className="divide-y divide-border">
                {emp.history.map((h) => (
                  <div key={h.id} className="px-4 py-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium capitalize">{h.event_type.replace("_", " ")}</span>
                      <span className="text-xs text-muted-foreground">{dateFmt(h.effective_date)}</span>
                    </div>
                    <div className="mt-0.5 text-muted-foreground">{String(h.from_value ?? "—")} → {String(h.to_value ?? "—")}</div>
                    {h.note && <div className="mt-0.5 text-xs text-muted-foreground">{h.note}</div>}
                  </div>
                ))}
              </div>
            ) : <p className="p-4 text-sm text-muted-foreground">No history recorded.</p>}
          </Section>
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          <Section title="Documents" action={canEdit && (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" data-testid="generate-doc-btn" onClick={() => navigate(`/hrms/documents/generate?entityType=employee&entityId=${id}`)}>
                <FileCog className="mr-1.5 h-3.5 w-3.5" />Generate Document
              </Button>
              <Button size="sm" variant="outline" data-testid="add-doc-btn" onClick={() => setAddingDoc((s) => !s)}>
                <Plus className="mr-1.5 h-3.5 w-3.5" />Add Document
              </Button>
            </div>
          )}>
            {addingDoc && (
              <div className="border-b border-border/60 bg-muted/30 p-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label>File *</Label>
                    <input type="file" data-testid="doc-file" className="mt-1 block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-primary-foreground"
                      onChange={(e) => setDocFile(e.target.files?.[0] || null)} />
                  </div>
                  <div><Label>Document Name (optional)</Label><Input data-testid="doc-name" className="mt-1" placeholder={docFile?.name} value={docForm.name} onChange={(e) => setDocForm((f) => ({ ...f, name: e.target.value }))} /></div>
                  <div><Label>Tags (comma-separated)</Label><Input data-testid="doc-tags" className="mt-1" value={docForm.tags} onChange={(e) => setDocForm((f) => ({ ...f, tags: e.target.value }))} /></div>
                  <div><Label>Expiry Date (optional)</Label><Input data-testid="doc-expiry" type="date" className="mt-1" value={docForm.expiry_date} onChange={(e) => setDocForm((f) => ({ ...f, expiry_date: e.target.value }))} /></div>
                  <div>
                    <Label>Visibility</Label>
                    <Select value={docForm.visibility} onValueChange={(v) => setDocForm((f) => ({ ...f, visibility: v }))}>
                      <SelectTrigger className="mt-1" data-testid="doc-visibility"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="all">Everyone</SelectItem><SelectItem value="hr_only">HR Only</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <UploadProgress percent={docProgress} />
                <div className="mt-3 flex justify-end gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setAddingDoc(false)} disabled={addDoc.isPending}>Cancel</Button>
                  <Button size="sm" data-testid="doc-submit" disabled={!docFile || addDoc.isPending} onClick={() => {
                    const fd = new FormData();
                    fd.append("file", docFile);
                    if (docForm.name) fd.append("name", docForm.name);
                    fd.append("tags", docForm.tags);
                    fd.append("expiry_date", docForm.expiry_date || "");
                    fd.append("visibility", docForm.visibility);
                    fd.append("entity_type", "employee");
                    fd.append("entity_id", id);
                    fd.append("folder_id", "fold-hr");
                    fd.append("uploaded_by", me?.id || "");
                    addDoc.mutate(fd);
                  }}>Upload</Button>
                </div>
              </div>
            )}
            {visibleDocs.length ? (
              <div className="divide-y divide-border">
                {visibleDocs.map((d) => (
                  <div key={d.id} data-testid={`doc-row-${d.id}`} onClick={() => openDmsFile(d.id)}
                    className="group flex w-full cursor-pointer items-center justify-between px-4 py-3 text-left text-sm hover:bg-muted/50">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{d.name}</span>
                      {d.visibility === "hr_only" && <StatusBadge family="slate" label="HR Only" />}
                      {d.expiry_date && <span className="text-xs text-muted-foreground">expires {dateFmt(d.expiry_date)}</span>}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground">{d.size} · v{d.version} · {relative(d.created_at)}</span>
                      <DmsFileActions file={d} queryKey={["dms-files", "employee", id]} canConvert={canEdit} canDelete={canDelete} />
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="p-4 text-sm text-muted-foreground">No documents on file.</p>}
          </Section>
        </TabsContent>

        <TabsContent value="leave" className="mt-4">
          {myLeave.length ? (
            <div className="overflow-hidden rounded-xl border border-border bg-card">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">Type</th><th className="px-4 py-3 font-semibold">Dates</th>
                  <th className="px-4 py-3 font-semibold">Days</th><th className="px-4 py-3 font-semibold">Status</th></tr></thead>
                <tbody>{myLeave.map((a) => (
                  <tr key={a.id} className="border-b border-border last:border-0">
                    <td className="px-4 py-3">{a.type}</td><td className="px-4 py-3 text-muted-foreground">{dateFmt(a.from)} – {dateFmt(a.to)}</td>
                    <td className="px-4 py-3">{a.days}</td><td className="px-4 py-3"><StatusBadge status={a.status} /></td>
                  </tr>))}</tbody>
              </table>
            </div>
          ) : <p className="text-sm text-muted-foreground">No leave records.</p>}
        </TabsContent>

        <TabsContent value="payslips" className="mt-4">
          {myPay.length ? (
            <div className="overflow-hidden rounded-xl border border-border bg-card">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">Month</th><th className="px-4 py-3 text-right font-semibold">Gross</th>
                  <th className="px-4 py-3 text-right font-semibold">Net</th><th className="px-4 py-3 font-semibold">Status</th></tr></thead>
                <tbody>{myPay.map((p) => (
                  <tr key={p.id} data-testid={`payslip-row-${p.id}`} onClick={() => setPayslip(p)} className="cursor-pointer border-b border-border last:border-0 hover:bg-muted/50">
                    <td className="px-4 py-3">{p.month}</td><td className="px-4 py-3 text-right">{money(p.gross)}</td>
                    <td className="px-4 py-3 text-right font-medium">{money(p.net)}</td><td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                  </tr>))}</tbody>
              </table>
            </div>
          ) : <p className="text-sm text-muted-foreground">No payslips.</p>}
        </TabsContent>

        <TabsContent value="assets" className="mt-4">
          {myAssets.length ? (
            <div className="overflow-hidden rounded-xl border border-border bg-card">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">Item</th><th className="px-4 py-3 font-semibold">Category</th>
                  <th className="px-4 py-3 font-semibold">Serial No.</th><th className="px-4 py-3 font-semibold">Assigned</th></tr></thead>
                <tbody>{myAssets.map((a) => (
                  <tr key={a.id} className="border-b border-border last:border-0">
                    <td className="px-4 py-3 font-medium">{a.name}</td><td className="px-4 py-3 text-muted-foreground">{a.category}</td>
                    <td className="px-4 py-3 text-muted-foreground">{a.serial_number}</td><td className="px-4 py-3">{dateFmt(a.assigned_date)}</td>
                  </tr>))}</tbody>
              </table>
            </div>
          ) : <p className="text-sm text-muted-foreground">No assets issued.</p>}
        </TabsContent>

        {canEdit && (
          <TabsContent value="perf-params" className="mt-4">
            <Section title="Performance Parameters" action={
              <Button size="sm" data-testid="add-param-btn" variant="outline"
                onClick={() => setDimensions((ds) => [...(ds || []), { id: `dim-custom-${Date.now()}`, category: "Custom", name: "", max_score: 5 }])}>
                <Plus className="mr-1.5 h-3.5 w-3.5" />Add Parameter
              </Button>
            }>
              {!dimensions ? (
                <p className="p-4 text-sm text-muted-foreground">Loading…</p>
              ) : (
                <>
                  <p className="border-b border-border/60 px-4 py-3 text-xs text-muted-foreground">
                    {perfTemplate?.is_custom ? "Custom parameters for this employee." : "Pre-filled from the role's default rubric — edit and save to make it custom to this employee."}
                  </p>
                  <div className="divide-y divide-border">
                    {dimensions.map((d, i) => (
                      <div key={d.id} className="grid grid-cols-12 items-center gap-2 px-4 py-2.5">
                        <Input data-testid={`param-category-${i}`} className="col-span-3" placeholder="Category" value={d.category}
                          onChange={(e) => setDimensions((ds) => ds.map((x, idx) => idx === i ? { ...x, category: e.target.value } : x))} />
                        <Input data-testid={`param-name-${i}`} className="col-span-6" placeholder="Parameter name" value={d.name}
                          onChange={(e) => setDimensions((ds) => ds.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x))} />
                        <Input data-testid={`param-max-${i}`} className="col-span-2" type="number" min={1} placeholder="Ideal score" value={d.max_score}
                          onChange={(e) => setDimensions((ds) => ds.map((x, idx) => idx === i ? { ...x, max_score: Number(e.target.value) || 1 } : x))} />
                        <button className="col-span-1 text-muted-foreground hover:text-red-600"
                          onClick={() => setDimensions((ds) => ds.filter((_, idx) => idx !== i))}><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end gap-2 border-t border-border/60 px-4 py-3">
                    <Button size="sm" data-testid="save-params-btn" onClick={() => saveTemplate.mutate()} disabled={saveTemplate.isPending || !dimensions.length}>
                      <SlidersHorizontal className="mr-1.5 h-3.5 w-3.5" />Save Parameters
                    </Button>
                  </div>
                </>
              )}
            </Section>
          </TabsContent>
        )}

        {canSeeHrOnly && (
          <TabsContent value="flags" className="mt-4">
            {flags.length >= 3 && (
              <div className="mb-4 flex items-center gap-2 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-400">
                <AlertTriangle className="h-4 w-4" />Termination Zone — {flags.length} red flags on record
              </div>
            )}
            <Section title="Red Flags" action={
              <Button size="sm" data-testid="add-flag-btn" variant="outline" onClick={() => setAddingFlag((s) => !s)}>
                <Flag className="mr-1.5 h-3.5 w-3.5" />Add Flag
              </Button>
            }>
              {addingFlag && (
                <div className="border-b border-border/60 bg-muted/30 p-4">
                  <div className="space-y-3">
                    <div><Label>Reason</Label><Input data-testid="flag-reason" className="mt-1" value={flagForm.reason} onChange={(e) => setFlagForm((f) => ({ ...f, reason: e.target.value }))} /></div>
                    <div><Label>Details</Label><Textarea data-testid="flag-details" className="mt-1" rows={3} value={flagForm.details} onChange={(e) => setFlagForm((f) => ({ ...f, details: e.target.value }))} /></div>
                  </div>
                  <div className="mt-3 flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setAddingFlag(false)}>Cancel</Button>
                    <Button size="sm" data-testid="flag-submit" disabled={!flagForm.reason || addFlag.isPending}
                      onClick={() => addFlag.mutate(flagForm)}>Record Flag</Button>
                  </div>
                </div>
              )}
              {flags.length ? (
                <div className="divide-y divide-border">
                  {flags.map((f) => (
                    <div key={f.id} className="px-4 py-3 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-red-600 dark:text-red-400">{f.reason}</span>
                        <span className="text-xs text-muted-foreground">{dateFmt(f.flagged_at)}</span>
                      </div>
                      {f.details && <div className="mt-0.5 text-muted-foreground">{f.details}</div>}
                    </div>
                  ))}
                </div>
              ) : <p className="p-4 text-sm text-muted-foreground">No flags on record.</p>}
            </Section>
          </TabsContent>
        )}
      </Tabs>

      {payslip && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6">
          <div className="no-print absolute right-6 top-6 flex gap-2">
            <Button variant="outline" onClick={() => window.print()}><Printer className="mr-1.5 h-4 w-4" />Print / Export PDF</Button>
            <Button variant="outline" size="icon" onClick={() => setPayslip(null)}><X className="h-4 w-4" /></Button>
          </div>
          <div className="print-area max-h-full w-full max-w-lg overflow-auto rounded-2xl border border-border bg-white p-8 shadow-2xl">
            <div className="mb-6 flex items-center justify-between border-b border-border pb-4">
              <div><div className="font-display text-lg font-bold">Payslip</div><div className="text-xs text-muted-foreground">{payslip.month}</div></div>
              <StatusBadge status={payslip.status} />
            </div>
            <div className="mb-4 text-sm"><span className="font-medium">{emp.name}</span> · {emp.designation}</div>
            <div className="space-y-1.5 text-sm">
              {(payslip.line_items || []).map((li, i) => (
                <div key={i} className="flex justify-between">
                  <span className="text-muted-foreground">{li.label}</span>
                  <span className={li.kind === "deduction" ? "text-red-500" : "text-emerald-600"}>{li.kind === "deduction" ? "- " : "+ "}{money(li.amount)}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-between border-t border-border pt-4 text-base font-bold">
              <span>Net Pay</span><span>{money(payslip.net)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
